import { useState, useEffect, useMemo, useRef } from 'react';
import websocketService from '../services/websocketService';
import { toast } from 'react-toastify';

/**
 * Custom hook for using WebSocket notifications in React components
 * 
 * This hook automatically initializes the WebSocket connection using the current
 * authentication token from tokenService. It doesn't require explicit token passing.
 * 
 * @param {string} userId - Optional user ID for WebSocket connection
 * @returns {Object} - WebSocket state and notifications
 */
const useWebSocket = (userId = null) => {
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState(null);

  const seenIdsRef = useRef(new Set());
  const listenerIdRef = useRef(null);
  const initializedRef = useRef(false);

  const unreadCount = useMemo(
    () => notifications.reduce((acc, n) => acc + (n.read ? 0 : 1), 0),
    [notifications]
  );

  useEffect(() => {
    let cancelled = false;

    const connectWebSocket = async () => {
      try {
        if (initializedRef.current) return;

        console.log('Initializing WebSocket with userId:', userId);
        await websocketService.initialize(userId);
        if (cancelled) return;

        initializedRef.current = true;
        setIsConnected(true);
        setError(null);
        console.log('WebSocket connected successfully');
        
        // Add listener for new notifications
        console.log('Adding notification listener to WebSocket');
        listenerIdRef.current = websocketService.addNotificationListener((data) => {
          console.log('Notification received in hook:', data);

          const key = data?.id || data?.title || JSON.stringify(data);
          
          if (seenIdsRef.current.has(key)) {
            console.log('Notification already exists, skipping');
            return;
          }
          seenIdsRef.current.add(key);
          
          // Mostrar alerta cuando llega una notificación
          try {
            toast.info(`Nueva notificación: ${data.title || 'Notificación recibida'}`, {
              toastId: key,
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true
            });
            console.log('Toast notification displayed');
          } catch (toastError) {
            console.error('Error showing toast notification:', toastError);
          }
          
          setNotifications(prevNotifications => {
            console.log('Updating notifications state');
            return [{ ...data, id: key, read: false }, ...prevNotifications];
          });
        });
        console.log('Notification listener added with ID:', listenerIdRef.current);
      } catch (err) {
        console.error('Failed to connect to WebSocket:', err);
        setError('Failed to connect to notification service');
        setIsConnected(false);
      }
    };

    connectWebSocket();

    // Cleanup function
    return () => {
      cancelled = true;
      if (listenerIdRef.current !== null) {
        websocketService.removeNotificationListener(listenerIdRef.current);
        listenerIdRef.current = null;
      }
      websocketService.disconnect();
      initializedRef.current = false;
      setIsConnected(false);
    };
  }, [userId]);

  /**
   * Mark a notification as read
   * @param {string} notificationId - ID of the notification to mark as read
   */
  const markAsRead = (notificationId) => {
    setNotifications(prevNotifications => 
      prevNotifications.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true } 
          : notification
      )
    );
    
    // Here you could also send a request to your API to mark the notification as read on the server
  };

  /**
   * Clear all notifications
   */
  const clearNotifications = () => {
    setNotifications([]);
    
    // Here you could also send a request to your API to clear notifications on the server
  };

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = () => {
    setNotifications(prevNotifications => 
      prevNotifications.map(n => ({ ...n, read: true }))
    );
  };

  return {
    isConnected,
    notifications,
    unreadCount,
    error,
    markAllAsRead
  };
};

export default useWebSocket;
